import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@fluentui/react-components';
import { AddFilled, PeopleRegular } from '@fluentui/react-icons';
import { ConfirmDialog, type TConfirmDialogState } from '@shared/components/dialogs/ConfirmDialog';
import { AccessDialog, type TAccessDialogState, AccessType } from '@modules/access';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { SignInContext } from '@modules/users';
import { ProjectTemplateDialog } from '@modules/mocks/projectTemplates';
import { StatusLabel } from '@shared/components/StatusLabel';
import { ListPageLayout } from '@shared/components/ListPageLayout';
import { Pagination } from '@shared/components/Pagination';
import { Path } from '@shared/components/Path';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks/useFolderPathAsBreadcrumbs.hook';
import FoldersNavDrawer from '@modules/items/components/nav/FoldersNavDrawer';
import type { IFolder, TPath } from '@modules/items/types/folders.types';
import { useProjects, useArchiveProject, useCreateProject } from '../api';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { usePageClamp } from '@shared/hooks/usePageClamp.hook';
import toPositiveInteger from '@shared/helpers/toPositiveInteger';
import { calculateRowsPerPage } from '@shared/helpers/calculateRowsPerPage';
import { getProjectPath, getProjectsPath } from '../paths';
import {
  PROJECTS_UI_MARGIN_TOP,
  PROJECT_STATUSES,
  DEFAULT_PROJECT_FILTER_STATUSES,
  PROJECT_ALL_STATUSES_FILTER,
} from '../constants';
import type { TProjectListItem } from '../types';
import ProjectDialog from './ProjectDialog';
import ProjectsTable from './ProjectsTable';
import ProjectsFilters from './ProjectsFilters';

const ProjectsContent = ({ folder }: { folder: IFolder }) => {
  const { user } = useContext(SignInContext);
  const { canEdit } = useCanEdit(folder.accessRight, { authorGroup: false });
  const parent = folder.resolvedPath[0]._id;
  const navigate = useNavigate();
  const { searchParams, changeMany } = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const statusValues = searchParams.get('status')?.split(',') ?? DEFAULT_PROJECT_FILTER_STATUSES;
  const status = PROJECT_STATUSES.filter((option) => statusValues.includes(option.value)).map((option) => option.value);
  const page = toPositiveInteger(Number(searchParams.get('page')), 1);
  const pageSize = toPositiveInteger(
    Number(searchParams.get('pageSize')),
    Number(calculateRowsPerPage(PROJECTS_UI_MARGIN_TOP))
  );
  const { data, error, isLoading } = useProjects({ parent, search, status, page, pageSize });
  usePageClamp(data, isLoading);
  const create = useCreateProject(parent);
  const archive = useArchiveProject();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Mock: organization.mock.projectTemplates puts a template picker before the create dialog.
  const projectTemplates = useContext(SignInContext).organization.mock?.projectTemplates ?? [];
  const [isProjectTemplateDialogOpen, setIsProjectTemplateDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState<TConfirmDialogState>();
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const canManageAccess = folder.accessRight >= AccessType.MANAGE;
  const fullPath: TPath = [...folder.resolvedPath, { ...folder, href: getProjectsPath(folder._id) }];

  const onArchive = (project: TProjectListItem) => {
    const dialog: TConfirmDialogState = {
      isOpen: true,
      title: `Archive ${TERMINOLOGY.project.lower}?`,
      content: `"${project.name}" will be moved to the archive.`,
      buttonLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        try {
          await archive.mutateAsync(project._id);
          setConfirm({ ...dialog, isOpen: false });
        } catch {
          /* The mutation shows the error; keep the confirmation open for retry. */
        }
      },
    };
    setConfirm(dialog);
  };
  return (
    <ListPageLayout nav={<FoldersNavDrawer folder={folder} fullPath={fullPath} selectedValue="projects" />}>
      <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
        <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
        {(canEdit || canManageAccess) && (
          <div className="flex gap-2">
            {canEdit && (
              <Button
                appearance="primary"
                size="medium"
                icon={<AddFilled />}
                onClick={() => (projectTemplates.length ? setIsProjectTemplateDialogOpen(true) : setIsCreateOpen(true))}
                className="w-fit"
              >
                New
              </Button>
            )}
            {canManageAccess && (
              <Button
                appearance="secondary"
                size="medium"
                icon={<PeopleRegular />}
                onClick={() =>
                  setAccessDialogState({ isOpen: true, id: folder._id, nodeType: 'FOLDER', name: folder.name })
                }
                className="w-fit"
              >
                Access
              </Button>
            )}
          </div>
        )}
        <ProjectsFilters
          search={search}
          statuses={status}
          onSearchChange={(value) => changeMany({ search: value || undefined, page: '1' })}
          onStatusesChange={(values) =>
            changeMany({ status: values.length ? values.join(',') : PROJECT_ALL_STATUSES_FILTER, page: '1' })
          }
        />
        <StatusLabel
          data={data}
          error={error}
          loadingMessage={`Loading ${TERMINOLOGY.project.lowerPlural}...`}
          errorMessage={`Failed to load ${TERMINOLOGY.project.lower} list!`}
          render={(result) =>
            result.items.length > 0 ? (
              <>
                <ProjectsTable projects={result.items} onArchive={onArchive} />
                <Pagination total={result.total} margin={PROJECTS_UI_MARGIN_TOP} />
              </>
            ) : (
              <p className="text-neutral-500 py-8">No {TERMINOLOGY.project.lowerPlural} found.</p>
            )
          }
        />
        <ProjectDialog
          isOpen={isCreateOpen}
          setIsOpen={setIsCreateOpen}
          rootItemName={folder.resolvedPath[0].name}
          currentUserId={user._id}
          onSave={async (values) => {
            const id = await create.mutateAsync(values);
            navigate(getProjectPath(id));
          }}
        />
        <ProjectTemplateDialog
          isOpen={isProjectTemplateDialogOpen}
          setIsOpen={setIsProjectTemplateDialogOpen}
          templates={projectTemplates}
          onCreateBlank={() => setIsCreateOpen(true)}
          onSelectTemplate={({ project }) => navigate(getProjectPath(project))}
        />
        <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
        <ConfirmDialog state={confirm} setState={setConfirm} />
      </div>
    </ListPageLayout>
  );
};

export default ProjectsContent;
